import React, {useCallback, useEffect, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore, useGate} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {LanguageSelector} from 'components/common/LanguageSelector';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {TitleLogoComponent} from 'components/ui-kit/TitleLogo';
import {LABELS} from 'core/constants/labels';

import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {doLogout} from 'models/auth';
import {Gate, resetPasswordRequestFx, state$} from 'models/password';

import {
    ButtonContainer,
    CustomDatamatrixIcon,
    Field,
    Fields,
    Form,
    FormDescription,
    LanguageWrapper,
    MainContainer,
    MessageWrapper,
    PasswordContent,
    PasswordTitle,
    ServiceTitle,
} from './styled';

export function RequestResetPasswordPage() {
    const {t, i18n} = useTranslation();
    const state = useStore(state$);
    useGate(Gate);

    const translateValidates = useCallback(
        (key: string, replace?: Record<string, any>) =>
            t(`pages.password.validations.${key}`, replace),
        [i18n.language],
    );

    const schema = useMemo(
        () =>
            yup.object().shape({
                email: yup
                    .string()
                    .email(translateValidates('email'))
                    .required(translateValidates('required')),
            }),
        [translateValidates],
    );

    const {control, handleSubmit, formState, errors} = useForm({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues: {email: ''},
    });

    const onLogin = () => history.push(ROUTE.LOGIN);

    useEffect(() => {
        doLogout(false);
    }, []);

    const onSubmit = async (data: {email: string}) => {
        try {
            await resetPasswordRequestFx({
                email: data.email,
                userName: data.email,
                firstName: data.email,
            });
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <>
            <div>
                <ServiceTitle size={4}>
                    <TitleLogoComponent />
                    {LABELS.name}
                </ServiceTitle>
                <LanguageWrapper>
                    <LanguageSelector />
                </LanguageWrapper>
            </div>
            <MainContainer>
                <PasswordContent>
                    {!state.success && (
                        <>
                            <PasswordTitle size={3}>
                                {t('pages.password.resetRequest.title')}
                            </PasswordTitle>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <FormDescription>
                                    {t('pages.password.resetRequest.description')}
                                </FormDescription>
                                <Fields>
                                    <Field isError={!!errors.email}>
                                        <span>
                                            {t('pages.password.resetRequest.fields.email.label')}
                                        </span>
                                        <Controller
                                            name="email"
                                            control={control}
                                            render={({onChange, value, name}) => (
                                                <Input
                                                    name={name}
                                                    value={value}
                                                    onChange={onChange}
                                                    placeholder={t(
                                                        'pages.password.resetRequest.fields.email.placeholder',
                                                    )}
                                                    disabled={state.pending}
                                                    hasError={!!errors.email}
                                                    isValid={
                                                        formState.dirtyFields.email && !errors.email
                                                    }
                                                />
                                            )}
                                        />
                                    </Field>
                                </Fields>

                                <ButtonContainer>
                                    <Button
                                        type="submit"
                                        isLoading={state.pending}
                                        disabled={!formState.isValid}
                                    >
                                        {t('pages.password.resetRequest.submitButton')}
                                    </Button>
                                    <Button onClick={onLogin} variant={ButtonVariant.Text}>
                                        {t('pages.password.resetRequest.loginButton')}
                                    </Button>
                                </ButtonContainer>
                            </Form>
                        </>
                    )}
                    {state.success && (
                        <MessageWrapper>
                            <PasswordTitle size={3}>
                                {t('pages.password.resetRequest.success.title')}
                            </PasswordTitle>
                            <FormDescription>
                                {t('pages.password.resetRequest.success.description')}
                            </FormDescription>
                            <Button onClick={onLogin}>
                                {t('pages.password.resetRequest.success.backButton')}
                            </Button>
                        </MessageWrapper>
                    )}
                </PasswordContent>
            </MainContainer>
        </>
    );
}
